/**
 * ProductLocationPanel - Shows where a product is stored in the warehouse
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Divider,
  Button,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Map as MapIcon,
  Inventory2 as StockIcon,
  Warning as WarningIcon,
  Height as HeightIcon,
  CalendarToday as DateIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { getProductLocations } from '../../services/locationApi';
import {
  ProductLocationsDetail,
  ProductLocationEntry,
  getHeightAccessibilityLabel,
  getHeightAccessibilityColor,
} from './pathTypes';
import RackDetailView from './RackDetailView';

interface ProductLocationPanelProps {
  productId: number;
  productName?: string;
  productCode?: string;
  warehouseId: number;
  warehouseName?: string;
  onLocationSelect?: (location: ProductLocationEntry) => void;
  onShowOnMap?: (locationIds: number[]) => void;
}

const ProductLocationPanel: React.FC<ProductLocationPanelProps> = ({
  productId,
  productName,
  productCode,
  warehouseId,
  warehouseName,
  onLocationSelect,
  onShowOnMap,
}) => {
  useTranslation();

  const [data, setData] = useState<ProductLocationsDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rackDetailOpen, setRackDetailOpen] = useState(false);
  const [selectedRackId, setSelectedRackId] = useState<number | null>(null);
  const [highlightedShelfLevel, setHighlightedShelfLevel] = useState<number | undefined>();

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadLocations defined below, stable deps
  }, [productId, warehouseId]);

  const loadLocations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await getProductLocations(productId, warehouseId);
      setData(result);
    } catch (err: any) {
      console.error('Error loading product locations:', err);
      setError(err.response?.data?.error || 'Nie udało się załadować lokalizacji produktu');
    } finally {
      setLoading(false);
    }
  };

  const handleShowOnMap = () => {
    if (data && onShowOnMap) {
      const locationIds = data.locations.map(loc => loc.location.id);
      onShowOnMap(locationIds);
    }
  };

  const handleOpenRackDetail = (entry: ProductLocationEntry) => {
    const rackId = entry.rack?.id || entry.location.id;
    setSelectedRackId(rackId);
    setHighlightedShelfLevel(entry.location.shelfLevel);
    setRackDetailOpen(true);
  };

  const renderLocationItem = (entry: ProductLocationEntry) => {
    const accessibilityColor = getHeightAccessibilityColor(entry.location.heightAccessibility);
    const status = entry.expiryStatus ?? 'none';
    const expired = status === 'expired';
    const warning = status === 'warning';
    const expiring = expired || warning;
    const iconColor: 'error' | 'warning' | 'action' = expired
      ? 'error'
      : warning
      ? 'warning'
      : 'action';
    const textColor = expired
      ? 'error.main'
      : warning
      ? 'warning.main'
      : 'text.secondary';
    const statusLabel = expired
      ? 'Po terminie'
      : warning && entry.daysToExpiry != null
      ? `Za ${entry.daysToExpiry} dni`
      : null;
    
    const listItemContent = (
      <>
        <ListItemIcon>
          <LocationIcon color={expired ? 'error' : warning ? 'warning' : 'primary'} />
        </ListItemIcon>
        
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                {entry.rack?.code || entry.location.code}
              </Typography>
              {entry.location.shelfLevel > 0 && (
                <Chip
                  size="small"
                  label={`P${entry.location.shelfLevel}`}
                  variant="outlined"
                />
              )}
              {entry.location.slotIndex > 0 && (
                <Chip
                  size="small"
                  label={`S${entry.location.slotIndex}`}
                  variant="outlined"
                />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <StockIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {entry.availableQuantity} dostępne
                    {entry.reservedQuantity > 0 && (
                      <span style={{ color: '#ff9800' }}> ({entry.reservedQuantity} zarezerwowane)</span>
                    )}
                  </Typography>
                </Box>
                
                {entry.location.shelfHeightCm > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HeightIcon fontSize="small" color="action" />
                    <Typography variant="body2">
                      {entry.location.shelfHeightCm} cm
                    </Typography>
                  </Box>
                )}
                
                {entry.batchNumber && (
                  <Typography variant="body2" color="text.secondary">
                    Partia: {entry.batchNumber}
                  </Typography>
                )}
                
                {entry.expiryDate && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {expiring && <WarningIcon fontSize="small" color={expired ? 'error' : 'warning'} />}
                    <DateIcon fontSize="small" color={iconColor} />
                    <Typography
                      variant="body2"
                      color={textColor}
                    >
                      {new Date(entry.expiryDate).toLocaleDateString('pl-PL')}
                    </Typography>
                    {statusLabel && (
                      <Chip
                        size="small"
                        label={statusLabel}
                        color={expired ? 'error' : 'warning'}
                        variant="filled"
                        sx={{ ml: 0.5, height: 20 }}
                      />
                    )}
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: accessibilityColor,
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  {getHeightAccessibilityLabel(entry.location.heightAccessibility)}
                </Typography>
              </Box>
            </Box>
          }
        />
      </>
    );
    
    const actionButton = (
      <Tooltip title="Pokaż przekrój regału">
        <IconButton
          edge="end"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenRackDetail(entry);
          }}
        >
          <MapIcon />
        </IconButton>
      </Tooltip>
    );
    
    return (
      <ListItem
        key={entry.stockId}
        disablePadding
        sx={{
          borderLeft: `4px solid ${accessibilityColor}`,
          mb: 1,
          borderRadius: 1,
          bgcolor: expiring ? 'warning.lighter' : 'background.paper',
        }}
        secondaryAction={actionButton}
      >
        {onLocationSelect ? (
          <ListItemButton onClick={() => onLocationSelect(entry)}>
            {listItemContent}
          </ListItemButton>
        ) : (
          <Box sx={{ px: 2, py: 1 }}>{listItemContent}</Box>
        )}
      </ListItem>
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ mt: 2 }}>
          Ładowanie lokalizacji...
        </Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={loadLocations}>
              Spróbuj ponownie
            </Button>
          }
        >
          {error}
        </Alert>
      </Paper>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6">
              Lokalizacje produktu
            </Typography>
            {(productName || productCode) && (
              <Typography variant="body2" color="text.secondary">
                {productName} {productCode && `(${productCode})`}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={loadLocations}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* Summary */}
      <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Łącznie
            </Typography>
            <Typography variant="h6">
              {data.totalQuantity} szt.
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Dostępne
            </Typography>
            <Typography variant="h6" color="success.main">
              {data.totalAvailable} szt.
            </Typography>
          </Box>
          {data.totalReserved > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary">
                Zarezerwowane
              </Typography>
              <Typography variant="h6" color="warning.main">
                {data.totalReserved} szt.
              </Typography>
            </Box>
          )}
          <Box>
            <Typography variant="caption" color="text.secondary">
              Lokalizacji
            </Typography>
            <Typography variant="h6">
              {data.locationsCount}
            </Typography>
          </Box>
        </Box>
        
        {onShowOnMap && data.locations.length > 0 && (
          <Button
            variant="outlined"
            startIcon={<MapIcon />}
            onClick={handleShowOnMap}
            sx={{ mt: 2 }}
            fullWidth
          >
            Pokaż wszystkie na mapie
          </Button>
        )}
      </Box>
      
      <Divider />
      
      {/* Locations list */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {data.locations.length > 0 ? (
          <List disablePadding>
            {data.locations.map(renderLocationItem)}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              Produkt nie znajduje się w magazynie {warehouseName || `#${warehouseId}`}
            </Typography>
          </Box>
        )}
      </Box>
      
      {/* Rack Detail Dialog */}
      <RackDetailView
        open={rackDetailOpen}
        onClose={() => {
          setRackDetailOpen(false);
          setSelectedRackId(null);
          setHighlightedShelfLevel(undefined);
        }}
        locationId={selectedRackId}
        highlightedShelfLevel={highlightedShelfLevel}
        highlightedProductId={productId}
      />
    </Paper>
  );
};

export default ProductLocationPanel;
